import { PricingStepWrapper } from '@/components/page-specific/pricing/pricing-step-wrapper'
import { TinyLoader } from '@/components/loaders'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { getStripePrices } from '@/utils/stripe'
import { useMemo, useState } from 'react'
import { PricingBox } from '@/components/page-specific/pricing/step-two/pricing-box'
import { Currencies, Intervals, PlanKey } from './types'
import { NextSeo } from 'next-seo'
import { loadStripe } from '@stripe/stripe-js/pure'
import { PublicEnv } from '@/frontend-env'
import { File } from 'lucide-react'

export const PricingStepTwo = ({
  plans,
}: {
  plans: Awaited<ReturnType<typeof getStripePrices>>
}) => {
  const stripe = useMemo(() => loadStripe(PublicEnv.NEXT_PUBLIC_STRIPE_PUBLIC_KEY), [])
  const [currency, setCurrency] = useState<Currencies>('usd')
  const [interval, setInterval] = useState<Intervals>('yearly')

  const filteredPlans = Object.keys(plans).filter((key) =>
    interval === 'monthly' ? key.startsWith('monthly') : key.startsWith('yearly')
  ) as PlanKey[]

  const handleIntervalChange = (event: { target: { checked: any } }) => {
    setInterval(event.target.checked ? 'yearly' : 'monthly')
  }

  return (
    <>
      <NextSeo title="v0 report's personal, consultant and business pricing plans" />
      <PricingStepWrapper motionKey="section-selection">
        <div>
          <section className="w-8/12 mx-auto my-16 hidden md:block">
            <div className="grid gap-3">
              <div className="flex flex-row items-center bg-sky-100 text-sky-600 font-semibold w-fit px-4 py-2 rounded-full">
                <File className="h-4 w-4 mr-2" />
                Google Docs included
              </div>
              <h1 className="font-semibold text-neutral-900 text-3xl ">
                Your Advanced AI Document Generator
              </h1>
            </div>
          </section>
          <div className="my-12 w-11/12 md:w-10/12 lg:w-8/12 mx-auto mb-48">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-5 w-full mb-12 flex items-center justify-between ">
              <div></div>

              <div>
                <div className="flex md:justify-center ">
                  <label className="mx-auto md:mr-auto p-1 border rounded-xl themeSwitcherTwo shadow-card relative inline-flex cursor-pointer select-none items-center justify-center bg-white">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={interval === 'yearly'}
                      onChange={handleIntervalChange}
                    />
                    <span
                      className={`flex px-10 items-center space-x-[6px] rounded-lg py-2 text-sm font-medium ${
                        interval === 'monthly'
                          ? 'text-primary bg-gray-200 border'
                          : 'text-body-color'
                      }`}
                    >
                      Monthly
                    </span>
                    <span
                      className={`flex px-10 items-center space-x-[6px] rounded-lg py-2 text-sm font-medium ${
                        interval === 'yearly' ? 'text-primary bg-gray-200' : 'text-body-color'
                      }`}
                    >
                      Yearly
                    </span>
                  </label>
                </div>
              </div>

              <div className="mx-auto md:mr-auto md:pl-36 w-6/12 md:w-fit">
                <Select
                  onValueChange={(value) => setCurrency(value as Currencies)}
                  defaultValue={'usd'}
                >
                  <SelectTrigger className="md:ml-auto w-full md:w-fit h-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usd">
                      $ <span className="ml-1">USD</span>
                    </SelectItem>
                    <SelectItem value="eur">
                      € <span className="ml-1">EUR</span>
                    </SelectItem>
                    <SelectItem value="gbp">
                      £ <span className="ml-1">GBP</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 ">
              {filteredPlans.map((planKey) => (
                <PricingBox
                  key={planKey}
                  planKey={planKey}
                  plans={plans}
                  currency={currency}
                  interval={interval}
                  stripe={stripe}
                />
              ))}
            </div>
          </div>
        </div>
      </PricingStepWrapper>
    </>
  )
}
